import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { browserTimeZone, gmtOffset, isValidTimeZone } from '@/utils/timezones';

/**
 * The current date and time in the school's time zone, ticking every second.
 *
 * The zone comes from the signed-in profile (`schoolTimezone`), so changing a
 * school's time zone moves every clock on the next profile refresh. Without a
 * usable zone — a platform account, or a bad stored value — it falls back to
 * the device's own zone and says so.
 */
export function LiveClock({ timeZone, className }: { timeZone?: string | null; className?: string }) {
  const usable = isValidTimeZone(timeZone);
  const zone = usable ? timeZone : browserTimeZone();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Re-aligned to the start of each second so the display never lags a
    // wall clock by a fraction and then skips.
    let timer: number;
    const schedule = () => {
      timer = window.setTimeout(() => {
        setNow(new Date());
        schedule();
      }, 1000 - (Date.now() % 1000));
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, []);

  const time = useMemo(
    () => new Intl.DateTimeFormat(undefined, { timeZone: zone, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    [zone],
  );
  const date = useMemo(
    () => new Intl.DateTimeFormat(undefined, { timeZone: zone, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    [zone],
  );

  return (
    <div
      role="timer"
      aria-label={`Current time, ${zone}`}
      className={clsx('rounded-xl border border-line bg-card px-4 py-3 text-left shadow-xs sm:text-right', className)}
    >
      <time dateTime={now.toISOString()} className="block text-[26px] font-semibold leading-none tracking-[-0.02em] text-ink tabular-nums">
        {time.format(now)}
      </time>
      <p className="mt-1.5 text-[13px] text-muted">{date.format(now)}</p>
      <p className="mt-0.5 text-[11.5px] text-subtle">
        {zone.replaceAll('_', ' ')} · {gmtOffset(zone, now)}
        {!usable && ' · your device'}
      </p>
    </div>
  );
}
